import { useState } from "react"
import { Lock, Mail, ShieldCheck, Sunrise, UserRound } from "lucide-react"
import { Link } from "react-router-dom"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, Card, SectionHeading, cx } from "../../components/ui"

/**
 * Only the digest is a choice.
 *
 * The other three emails are how the product works: a sign-in code you asked
 * for, being told you now have access to something, and being told someone is
 * waiting on you. Making those optional would mean an issue could sit assigned
 * to a person who never finds out.
 */
export function NotificationSettings() {
	const { user, refreshUser, toast, timezone } = useApp()
	const [busy, setBusy] = useState(false)

	const digestAvailable = user?.plan?.digest
	const digestOn = Boolean(user?.settings?.daily_digest) && digestAvailable

	async function toggleDigest(next) {
		setBusy(true)
		try {
			await api.updateMe({ settings: { daily_digest: next } })
			await refreshUser()
			toast(next ? "Daily summary switched on" : "Daily summary switched off", "success")
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className="space-y-5">
			<Card className="p-5">
				<SectionHeading
					title="Daily summary"
					description="One email a morning with everything still open and anything waiting on you."
					className="mb-4"
				/>

				{digestAvailable ? (
					<button
						type="button"
						onClick={() => toggleDigest(!digestOn)}
						disabled={busy}
						className={cx(
							"flex w-full items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors",
							digestOn
								? "border-brand-300 bg-brand-50"
								: "border-ink-200 bg-white hover:bg-ink-50"
						)}
					>
						<Sunrise
							size={17}
							className={cx(
								"mt-0.5 shrink-0",
								digestOn ? "text-brand-700" : "text-ink-400"
							)}
							aria-hidden
						/>
						<span className="min-w-0 flex-1">
							<span className="block text-[0.8125rem] font-medium text-ink-900">
								Send me a daily summary
							</span>
							<span className="mt-0.5 block text-2xs leading-relaxed text-ink-600">
								Sent each morning, with times shown in {timezone}. Nothing is sent on
								a day with nothing outstanding.
							</span>
						</span>
						<Switch on={digestOn} />
					</button>
				) : (
					<div className="flex items-start gap-3 rounded-lg border border-ink-200 bg-ink-50 px-3.5 py-3">
						<Lock size={16} className="mt-0.5 shrink-0 text-ink-400" aria-hidden />
						<div className="min-w-0 flex-1">
							<p className="text-[0.8125rem] font-medium text-ink-900">
								Available on Plus and Max
							</p>
							<p className="mt-0.5 text-2xs leading-relaxed text-ink-600">
								The daily summary is not part of the Free plan.
							</p>
						</div>
						<Button as={Link} to="/settings/billing" size="sm" variant="subtle">
							See plans
						</Button>
					</div>
				)}
			</Card>

			<Card className="p-5">
				<SectionHeading
					title="Always sent"
					description="These three are part of how Loomina works and cannot be switched off."
					className="mb-4"
				/>
				<ul className="space-y-2.5">
					<Always
						icon={ShieldCheck}
						title="Your sign-in code"
						body="The six-digit code you need to finish signing in."
					/>
					<Always
						icon={Mail}
						title="You have been added to an issue"
						body="Sent when someone gives you access to a thread, so you know it exists."
					/>
					<Always
						icon={UserRound}
						title="Someone asked you to respond"
						body="Sent when an issue is assigned to you. Without it, an issue could sit waiting on a person who never finds out."
					/>
				</ul>
			</Card>
		</div>
	)
}

function Always({ icon: Icon, title, body }) {
	return (
		<li className="flex items-start gap-3 rounded-lg bg-ink-50 px-3.5 py-2.5">
			<Icon size={15} className="mt-0.5 shrink-0 text-ink-400" aria-hidden />
			<div>
				<p className="text-[0.8125rem] font-medium text-ink-900">{title}</p>
				<p className="mt-0.5 text-2xs leading-relaxed text-ink-600">{body}</p>
			</div>
		</li>
	)
}

function Switch({ on }) {
	return (
		<span
			className={cx(
				"mt-0.5 flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors",
				on ? "bg-brand-600" : "bg-ink-300"
			)}
			aria-hidden
		>
			<span
				className={cx(
					"h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
					on && "translate-x-4"
				)}
			/>
		</span>
	)
}
